import flow from 'lodash/flow';
import { getServerValidationErrors, validate } from '../validators';
import { commonQuestionState } from '../response/reducer';
import { scaleLinear } from 'd3-scale';
import {
  checkCompletion,
  minMarkers,
  requiredSticky,
  resetRowValids,
} from './validators';

import { prefillHeatmap } from '../prefillAnswers';

const reducer = props => (state, action) => {
  if (state === undefined) {
    return flow(
      commonQuestionState(props),
      getServerValidationErrors(props),
      addHeatMapState(props),
      prefillHeatmap(props),
      validate({ type: 'INIT' })
    )({});
  }

  switch (action.type) {
    case 'ADD_MARKER':
      return flow(
        touchRow(action),
        addMarker(action),
        initializeNote(action, props._type === 'Survey::StickyNote'),
        initializeTime(action, props._type === 'Survey::TimedHeatMap'),
        selectMarker(action, props._type === 'Survey::StickyNote'),
        validate(action)
      )(state);

    case 'REMOVE_MARKER':
      return flow(
        removeMarker(action),
        deselect,
        touchRow(action),
        validate(action)
      )(state);

    case 'SELECT_MARKER':
      return flow(selectRow(action), selectMarker(action), validate(action))(
        state
      );

    case 'DESELECT':
      return flow(deselect, validate(action))(state);

    case 'UPDATE_NOTE':
      return flow(updateNote(action), validate(action))(state);

    case 'SELECT_ROW':
      return flow(
        touchRow(action),
        selectRow(action),
        deselect,
        validate(action)
      )(state);

    case 'SELECT_NEXT_ROW':
      return flow(
        touchRow(action),
        selectNextRow(),
        deselect,
        validate(action)
      )(state);

    case 'SET_DOMAIN':
      return flow(setDomain(action), validate(action))(state);

    case 'BEGIN_COUNTDOWN':
      return flow(beginCountdown)(state);

    case 'OPEN':
      return flow(begin)(state);

    case 'CLOSE':
      return flow(touchRow(action), validate(action))(state);

    case 'COUNT':
      return flow(count(action))(state);

    case 'INIT':
      return flow(validate(action))(state);

    case 'SUBMIT':
      return flow(touchAllRows, validate(action))(state);

    default:
      return state;
  }
};

export default reducer;

const addHeatMapState = props => state => ({
  ...state,
  currentRow: 0,
  markers: {},
  markerValids: {},
  selected: null,
  rows: props.rows.map(row => ({
    isTouched: false,
    name: row.name,
    keys: [],
  })),
  rowValids: props.rows.map(() => true),
  validators: [
    resetRowValids,
    minMarkers(props.min_number_of_fields),
    // maxMarkers(props.number_of_fields),
    requiredSticky(props._type === 'Survey::StickyNote' && props.required),
    checkCompletion,
  ],
  xScale: scaleLinear().range([0, 100]),
  yScale: scaleLinear().range([0, 100]),
  completionStatus: 'waiting', // 'waiting', 'in-progress', 'done'

  // sticky note
  notes: {},

  // timed heatmap
  times: {},
  countdownStatus: 'waiting', // 'waiting', 'counting', 'active', 'done' ?
  startTime: null,
  timer: 3,
});

export const addMarker = ({ max, rowIndex, timestamp: key, x, y }) => state => {
  // Short circuit if we're still counting down in a timed question
  if (state.countdownStatus === 'counting') {
    return state;
  }

  const marker = {
    key,
    x: state.xScale(x),
    y: state.yScale(y),
  };

  return {
    ...state,
    rows: state.rows.map(
      (row, idx) =>
        idx === rowIndex
          ? {
              ...row,
              keys:
                !max || row.keys.length < max
                  ? row.keys.concat([key])
                  : row.keys
                      .slice(row.keys.length - max + 1, row.keys.length)
                      .concat([key]),
            }
          : row
    ),
    markers: {
      ...state.markers,
      [key]: marker,
    },
    markerValids: {
      ...state.markerValids,
      [key]: true,
    },
  };
};

export const initializeNote = ({ timestamp: key }, test = true) => state =>
  test
    ? {
        ...state,
        notes: {
          ...state.notes,
          [key]: '',
        },
      }
    : state;

export const initializeTime = ({ timestamp }, test = true) => state =>
  test
    ? {
        ...state,
        times: {
          ...state.times,
          [timestamp]: timestamp - state.startTime,
        },
      }
    : state;

export const selectMarker = ({ timestamp: key }, test = true) => state =>
  test
    ? {
        ...state,
        selected: key,
      }
    : state;

export const deselect = state => ({
  ...state,
  selected: null,
});

export const removeMarker = ({ timestamp: key, rowIndex }) => state => ({
  ...state,
  rows: state.rows.map(
    (row, idx) =>
      idx === rowIndex
        ? {
            ...row,
            keys: row.keys.filter(k => k !== key),
          }
        : row
  ),
});

export const updateNote = ({ timestamp: key, value }) => state => ({
  ...state,
  notes: {
    ...state.notes,
    [key]: value,
  },
});

export const selectRow = ({ rowIndex }) => state => ({
  ...state,
  currentRow: rowIndex,
});

export const touchRow = () => state => ({
  ...state,
  rows: state.rows.map(
    (row, idx) =>
      idx === state.currentRow
        ? {
            ...row,
            isTouched: true,
          }
        : row
  ),
});

export const touchAllRows = state => ({
  ...state,
  rows: state.rows.map(row => ({
    ...row,
    isTouched: true,
  })),
});

export const begin = state => ({
  ...state,
  completionStatus: 'in-progress',
});

export const selectNextRow = () => state => ({
  ...state,
  currentRow: state.currentRow + 1,
});

export const setDomain = ({ width, height }) => state => {
  state.xScale.domain([0, width]);
  state.yScale.domain([0, height]);
  return {
    ...state,
    // let redux know we want to re-render
    domainTouched: Date.now(),
  };
};

export const beginCountdown = state => ({
  ...state,
  countdownStatus: state.countdownStatus === 'active' ? 'active' : 'counting',
});

export const count = action => state => {
  if (state.timer <= 1) {
    return {
      ...state,
      countdownStatus: 'active',
      startTime: Date.now(),
    };
  } else {
    window.setTimeout(action.cb, action.timeout);
    return {
      ...state,
      timer: state.timer - 1,
    };
  }
};
